import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "title",
    "subtitle",
    "progressText",
    "progressBar",
    "successCount",
    "errorCount",
    "warningCount",
    "totalCount",
    "generationLog",
    "estimatedTime",
    "statusMessage",
    "status",
    "progress",
    "stats",
    "continueButton",
    "icon",
  ];
  static values = {
    generatorId: Number,
    url: String,
  };

  connect() {
    console.log("Report card polling controller connected");
    this.startPolling();
    this.pollAttempts = 0;
    this.maxPollAttempts = 60;
    this.isPolling = false;
    this.beforeUnloadHandler = () => this.stopPolling();
    window.addEventListener("beforeunload", this.beforeUnloadHandler);
  }

  disconnect() {
    this.stopPolling();
    if (this.beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
    }
  }
  startPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
    if (!this.isPolling) {
      this.isPolling = true;
      console.log("Starting polling...");
      this.pollInterval = setInterval(() => {
        this.pollProgress();
      }, 2000); // Polling plus fréquent pour une meilleure réactivité
    }
  }

  stopPolling(data) {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
    this.isPolling = false;
    console.log("Polling stopped");

    if (!data) return;
    // A failed run renders no warnings section: reading it unguarded threw
    // here, and the error list below was never filled.
    const warningsSection = document.getElementById("warnings-section");
    if (warningsSection)
      warningsSection.style.display =
        data.warning_messages_count > 0 ? "block" : "none";
    const warningsList = document.getElementById("warnings-list");
    const warningTemplate = document.getElementById("warning-template");
    if (warningsList && warningTemplate && Array.isArray(data.warning_messages)) {
      warningsList.innerHTML = "";
      data.warning_messages.slice(0, 5).forEach((warning) => {
        const warningEntry = warningTemplate.content.cloneNode(true);
        warningEntry.querySelector(".title").textContent = warning.title || "";
        if (warning.student) {
          warningEntry.querySelector(".studentName").textContent =
            warning.student.name || "";
        } else {
          this.removeStudentName(warningEntry);
        }
        warningsList.appendChild(warningEntry);
      });

      const moreCount = data.warning_messages.length - 5;
      const moreElement = document.getElementById("show-more-warnings");
      if (moreCount > 0) {
        if (moreElement) {
          moreElement.style.display = "block";
          moreElement.querySelector(".moreCount").textContent = moreCount;
          moreElement.onclick = () => {
            data.warning_messages.slice(5).forEach((warning) => {
              const warningEntry = warningTemplate.content.cloneNode(true);
              warningEntry.querySelector(".title").textContent =
                warning.title || "";
              if (warning.student) {
                warningEntry.querySelector(".studentName").textContent =
                  warning.student.name || "";
              } else {
                this.removeStudentName(warningEntry);
              }
              warningsList.appendChild(warningEntry);
            });
            moreElement.style.display = "none";
          };
        }
      } else {
        if (moreElement) moreElement.style.display = "none";
      }
    }

    const errorsSection = document.getElementById("errors-section");
    if (errorsSection)
      errorsSection.style.display =
        data.failed_errors_count > 0 ? "block" : "none";
    const errorsList = document.getElementById("errors-list");
    const errorTemplate = document.getElementById("error-template");
    if (errorsList && errorTemplate && Array.isArray(data.failed_errors)) {
      errorsList.innerHTML = "";
      data.failed_errors.slice(0, 5).forEach((error) => {
        const errorEntry = errorTemplate.content.cloneNode(true);
        errorEntry.querySelector(".title").textContent =
          error.title || error.type || "Erreur";
        if (error.student) {
          errorEntry.querySelector(".studentName").textContent =
            error.student.name || "";
        } else {
          this.removeStudentName(errorEntry);
        }
        errorsList.appendChild(errorEntry);
      });

      const moreErrorCount = data.failed_errors.length - 5;
      const moreErrorElement = document.getElementById("show-more-errors");
      if (moreErrorCount > 0) {
        if (moreErrorElement) {
          moreErrorElement.style.display = "block";
          moreErrorElement.querySelector(".errorCount").textContent =
            moreErrorCount;
          moreErrorElement.onclick = () => {
            data.failed_errors.slice(5).forEach((error) => {
              const errorEntry = errorTemplate.content.cloneNode(true);
              errorEntry.querySelector(".title").textContent =
                error.title || error.type || "Erreur";
              if (error.student) {
                errorEntry.querySelector(".studentName").textContent =
                  error.student.name || "";
              } else {
                this.removeStudentName(errorEntry);
              }
              errorsList.appendChild(errorEntry);
            });
            moreErrorElement.style.display = "none";
          };
        }
      } else {
        if (moreErrorElement) moreErrorElement.style.display = "none";
      }
    }
  }

  // Without a student, the name goes and so does the « - » before it.
  removeStudentName(entry) {
    const name = entry.querySelector(".studentName");
    if (!name) return;
    const dash = name.previousSibling;
    if (dash && dash.nodeType === Node.TEXT_NODE) dash.textContent = " ";
    name.remove();
  }

  pollProgress() {
    this.pollAttempts++;
    if (this.pollAttempts >= this.maxPollAttempts) {
      this.showTimeoutMessage();
      this.stopPolling();
      return;
    }
    const url =
      this.urlValue +
      (this.urlValue.includes("?") ? "&" : "?") +
      "t=" +
      Date.now();
    fetch(url, {
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => {
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
      })
      .then((data) => {
        this.updateProgress(data);
        if (data.status === "success" || data.status === "failed") {
          this.stopPolling(data);
        }
      })
      .catch((error) => {
        console.error("Erreur lors du polling:", error);
      });
  }

  updateProgress(data) {
    console.log("Updating progress with data:", data);

    // Compteurs
    if (this.hasSuccessCountTarget)
      this.successCountTarget.textContent = data.valid_bulletins || 0;
    if (this.hasErrorCountTarget)
      this.errorCountTarget.textContent = data.failed_errors_count || 0;
    if (this.hasWarningCountTarget)
      this.warningCountTarget.textContent = data.warning_messages_count || 0;
    if (this.hasTotalCountTarget)
      this.totalCountTarget.textContent = data.total_bulletins || 0;

    // Barre de progression basée sur le pourcentage réel
    if (this.hasProgressBarTarget) {
      const progressPercent = data.progress_percentage || 0;
      this.progressBarTarget.style.width = `${progressPercent}%`;

      // Green while it runs, red as soon as a critical error stops it. Set on
      // the style: the bar's inline background beat the bg-* classes, so it
      // stayed in the module's slate whatever the state.
      // Finished with warnings: the warning colour (09/10/2026).
      const warned =
        data.progress_percentage >= 100 && data.warning_messages_count > 0;
      this.progressBarTarget.style.background =
        data.status === "failed"
          ? "var(--g4s-danger-600)"
          : warned
            ? "var(--g4s-warning)"
            : "var(--g4s-green-600)";
    }

    // Texte de progression
    if (this.hasProgressTextTarget) {
      if (data.current_step_message) {
        this.progressTextTarget.textContent = data.current_step_message;
      } else {
        this.progressTextTarget.textContent = `${data.valid_bulletins || 0}/${data.total_bulletins || 0} bulletins valides`;
      }
    }

    // Message de statut
    if (this.hasStatusMessageTarget) {
      this.statusMessageTarget.textContent =
        data.current_step_message || "Vérification en cours...";
    }

    // Mise à jour des étapes visuelles
    this.updateStepIndicators(data);

    // Temps estimé
    if (this.hasEstimatedTimeTarget) {
      this.estimatedTimeTarget.textContent =
        (data.verification_time || 0) + "s";
    }

    // Log détaillé
    if (this.hasGenerationLogTarget) {
      this.updateGenerationLog(data);
    }

    // Mise à jour de l'interface selon l'état
    this.updateInterfaceState(data);
  }

  updateStepIndicators(data) {
    const stepName = data.current_step_name || "initialization";
    const progressPercent = data.progress_percentage || 0;

    // Réinitialiser tous les indicateurs
    // [this.step1Target, this.step2Target, this.step3Target].forEach((target, index) => {
    //   if (target) {
    //     target.className = target.className.replace(/bg-\w+-\d+/, 'bg-gray-300')
    //   }
    // })

    // Mettre à jour selon la progression
    // if (progressPercent >= 33 && this.hasStep1Target) {
    //   this.step1Target.className = this.step1Target.className.replace(/bg-gray-300/, 'bg-blue-500')
    // }
    // if (progressPercent >= 66 && this.hasStep2Target) {
    //   this.step2Target.className = this.step2Target.className.replace(/bg-gray-300/, 'bg-blue-500')
    // }
    // if (progressPercent >= 100 && this.hasStep3Target) {
    //   this.step3Target.className = this.step3Target.className.replace(/bg-gray-300/, 'bg-cta')
    // }
  }

  updateGenerationLog(data) {
    this.generationLogTarget.innerHTML = "";

    // Déterminer le statut basé sur les données
    const hasValidBulletins = data.valid_bulletins > 0;
    const hasErrors = data.failed_errors && data.failed_errors.length > 0;
    const hasWarnings =
      data.warning_messages && data.warning_messages.length > 0;
    const isCompleted = data.progress_percentage >= 100;

    if (data.status === "failed") {
      this.addLogEntry(`❌ ${data.current_step_message || ""}`, "error");
    } else if (isCompleted) {
      if (hasValidBulletins && !hasErrors) {
        this.addLogEntry("✅ Génération terminée avec succès", "success");
        if (hasWarnings) {
          this.addLogEntry(
            `⚠️ ${data.warning_messages_count} avertissements détectés`,
            "warning",
          );
        }
      } else if (hasValidBulletins && hasErrors) {
        this.addLogEntry("⚠️ Génération terminée avec des erreurs", "warning");
        this.addLogEntry(
          `✅ ${data.valid_bulletins} bulletins valides générés`,
          "success",
        );
        this.addLogEntry(
          `❌ ${data.failed_errors_count} erreurs critiques`,
          "error",
        );
      } else if (hasErrors && !hasValidBulletins) {
        this.addLogEntry(
          "❌ Génération échouée - Aucun bulletin valide",
          "error",
        );
        this.addLogEntry(
          `❌ ${data.failed_errors_count} erreurs critiques à corriger`,
          "error",
        );
      } else if (!hasValidBulletins) {
        this.addLogEntry(
          "⚠️ Génération terminée - Aucun bulletin valide",
          "warning",
        );
        if (hasWarnings) {
          this.addLogEntry(
            `⚠️ ${data.warning_messages_count} avertissements détectés`,
            "warning",
          );
        }
      }
    } else {
      this.addLogEntry(
        `⏳ ${data.current_step_message || "En attente de progression..."}`,
        "info",
      );
    }

    // Ajouter les détails des erreurs si présentes
    if (hasErrors && data.failed_errors) {
      data.failed_errors.slice(0, 3).forEach((error) => {
        this.addLogEntry(
          `• ${error.title || error.type}${error.student ? " - " + error.student.name : ""}`,
          "error",
        );
      });
      if (data.failed_errors.length > 3) {
        this.addLogEntry(
          `... et ${data.failed_errors.length - 3} autres erreurs`,
          "error",
        );
      }
    }
  }

  updateInterfaceState(data) {
    if (data.status === "failed") {
      this.showStopped(data);
      return;
    }
    const hasValidBulletins = data.valid_bulletins > 0;
    const hasErrors = data.failed_errors && data.failed_errors.length > 0;
    const hasWarnings =
      data.warning_messages && data.warning_messages.length > 0;
    const isCompleted = data.progress_percentage >= 100; // Considérer comme terminé à 100%
    const isSuccess =
      data.status === "success" || (hasValidBulletins && isCompleted);

    // Mise à jour du titre et sous-titre
    if (this.hasTitleTarget) {
      this.titleTarget.textContent = "Vérification et Validation des bulletins";
    }

    if (this.hasSubtitleTarget) {
      if (isCompleted) {
        if (hasValidBulletins && !hasErrors && !hasWarnings) {
          this.subtitleTarget.textContent = `${data.valid_bulletins} bulletins générés avec succès`;
          this.subtitleTarget.className = "text-cta";
        } else if (hasValidBulletins && hasWarnings) {
          this.subtitleTarget.textContent = `${data.valid_bulletins} bulletins valides avec des avertissements`;
          this.subtitleTarget.className = "text-yellow-600";
        } else {
          this.subtitleTarget.textContent = "Aucun bulletin valide généré";
          this.subtitleTarget.className = "text-red-600";
        }
      } else {
        this.subtitleTarget.textContent =
          data.current_step_message || "Génération en cours...";
        this.subtitleTarget.className = "text-gray-600";
      }
    }

    // Mise à jour de l'icône principale
    // The icon target exists in the three cycles; `.w-16.h-16` only in secondary.
    const iconContainer = this.hasIconTarget
      ? this.iconTarget
      : this.element.querySelector(".w-16.h-16");
    if (iconContainer && isCompleted) {
      iconContainer.style.background =
        hasValidBulletins && hasWarnings
          ? "var(--g4s-warning-bg)"
          : hasValidBulletins && !hasErrors
            ? "var(--ds-primary-tint)"
            : "var(--g4s-danger-bg)";
      const currentIcon = iconContainer.querySelector("svg");
      if (currentIcon) {
        if (hasValidBulletins && !hasErrors && !hasWarnings) {
          // Icône de succès (vert)
          currentIcon.outerHTML = `
            <svg class="w-8 h-8 text-cta" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"></path>
            </svg>
          `;
        } else if (hasValidBulletins && hasWarnings) {
          // Icône d'avertissement (jaune)
          currentIcon.outerHTML = `
            <svg class="w-8 h-8 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
            </svg>
          `;
        } else {
          // Icône d'erreur (rouge)
          currentIcon.outerHTML = `
            <svg class="w-8 h-8 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
            </svg>
          `;
        }
      }
    }

    // Mise à jour des boutons d'action
    this.updateActionButtons(
      data,
      hasValidBulletins,
      hasErrors,
      hasWarnings,
      isCompleted,
      isSuccess,
    );
  }

  // A critical error stopped the run: the spinner gives way to a red cross,
  // whatever the percentage reached, and nothing leads to step 3.
  showStopped(data) {
    if (this.hasIconTarget) {
      this.iconTarget.style.color = "var(--g4s-danger-600)";
      this.iconTarget.style.background = "var(--g4s-danger-bg)";
      this.iconTarget.innerHTML = `
        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20" width="32" height="32">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
        </svg>
      `;
    }
    if (this.hasSubtitleTarget) {
      this.subtitleTarget.textContent = data.current_step_message || "";
      this.subtitleTarget.style.color = "var(--g4s-danger-600)";
    }
    const actionButton = document.querySelector(".action-button__step2");
    if (actionButton) actionButton.outerHTML = `<span class="action-button__step2"></span>`;
  }

  updateActionButtons(
    data,
    hasValidBulletins,
    hasErrors,
    hasWarnings,
    isCompleted,
    isSuccess,
  ) {
    const actionButton = document.querySelector(".action-button__step2");
    if (!actionButton) return;

    const generatorId = this.generatorIdValue;

    // Durant la génération — pas de bouton
    if (!isCompleted) {
      actionButton.outerHTML = `<span class="action-button__step2"></span>`;
      return;
    }

    if (isSuccess && hasValidBulletins && !hasWarnings && !hasErrors) {
      // Succès complet — bouton vert
      actionButton.outerHTML = `
        <a href="/schools/docs/report_cards/${generatorId}/step3" class="btn btn-cta action-button__step2">
          Voir les résultats
          <svg class="w-5 h-5 ml-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd"></path>
          </svg>
        </a>
      `;
    } else if (hasValidBulletins && hasWarnings) {
      // Avertissements — bouton jaune
      actionButton.outerHTML = `
        <a href="/schools/docs/report_cards/${generatorId}/step3" class="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-2 rounded-lg transition-colors font-medium flex items-center action-button__step2">
          <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
          </svg>
          Continuer malgré les problèmes (${data.valid_bulletins} bulletins valides)
        </a>
      `;
    } else if (!hasValidBulletins) {
      // Aucun bulletin valide — bouton rouge désactivé
      actionButton.outerHTML = `
        <button type="button" class="bg-red-500 text-white px-6 py-2 rounded-lg opacity-50 cursor-not-allowed flex items-center action-button__step2" disabled>
          <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path>
          </svg>
          Impossible de continuer - Aucun bulletin valide
        </button>
      `;
    }
  }

  addLogEntry(message, type = "info") {
    const logEntry = document.createElement("div");
    logEntry.className = `text-${this.getTypeColor(type)}-600`;
    logEntry.textContent = message;
    this.generationLogTarget.appendChild(logEntry);
    this.generationLogTarget.scrollTop = this.generationLogTarget.scrollHeight;
  }

  getTypeColor(type) {
    const colors = {
      info: "gray",
      success: "green",
      warning: "yellow",
      error: "red",
    };
    return colors[type] || "gray";
  }

  showTimeoutMessage() {
    if (this.hasGenerationLogTarget) {
      this.addLogEntry(
        "⏳ Délai d’attente dépassé - Vérification manuelle requise",
        "error",
      );
    }
  }

  // Méthode pour naviguer vers l'étape 3
  navigateToStep3(event) {
    event.preventDefault();
    this.stopPolling();
    window.location.href = event.currentTarget.href;
  }
}
